/** @jsxImportSource @emotion/react */
import { SerializedStyles, css } from '@emotion/react';
import { FC, useState } from 'react';
import TrainingVideoIcon from '../../../icons/TrainingVideo';
import { Box, Link } from '@mui/material';
import { QSTypography } from '../../atoms';
import { ReactComponent as ArrowOutwardIcon } from '../../../assets/svg/arrow-outward.svg';

interface TrainingVideoTileProps {
  videoUrl: string;
  thumbnailUrl: string;
  title?: string;
  subtitle?: string;
  learnMoreHref?: string;
  videoContainerStyles?: SerializedStyles;
  thumbnailStyles?: SerializedStyles;
  playBtnTitle?: string;
  playBtnTitleWidth?: number;
}

const TrainingVideoTile: FC<TrainingVideoTileProps> = ({
  subtitle,
  thumbnailUrl,
  title,
  videoUrl,
  learnMoreHref,
  videoContainerStyles,
  thumbnailStyles,
  playBtnTitle,
  playBtnTitleWidth = 42,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Link
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        flex: 1 0 0;
        min-width: 270px;
        ${videoContainerStyles}
      `}
      href={videoUrl}
      target="_blank"
      rel="nonreferrer"
      underline="none"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        css={css`
          position: relative;
          width: 100%;
        `}
      >
        <img
          src={thumbnailUrl}
          alt="training video"
          css={css`
            width: 100%;
            height: 193px;
            object-fit: cover;
            border-radius: 12px;
            transform: ${isHovered ? 'scale(1.01)' : ''};
            transition: 0.3s all;
            ${thumbnailStyles}
          `}
        />
        <Box
          css={css`
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            display: flex;
            padding: 8px;
            justify-content: center;
            align-items: center;
            gap: ${isHovered ? '8px' : '0px'};
            border-radius: 12px;
            background: rgba(45, 45, 45, 0.4);
            backdrop-filter: blur(10px);
            &:hover {
              box-shadow: none;
              background: linear-gradient(0deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.3) 100%), rgba(0, 0, 0, 0.2);
            }
          `}
        >
          <TrainingVideoIcon
            css={css`
              color: #fff;
            `}
            size={32}
          />
          <QSTypography
            css={css`
              color: #fff;
              font-family: Inter;
              font-size: 16px;
              font-weight: 680;
              line-height: 128%;
              transform: ${isHovered
                ? 'translate(-50%, -50%) translateX(50%) translateY(50%)'
                : 'translate(-50%, -50%)'};
              opacity: ${isHovered ? '1' : '0'};
              width: ${isHovered ? `${playBtnTitleWidth}px` : '0px'};
              transition: opacity 0.6s ease, transform 0.6s ease, width 0.6s ease;
              white-space: nowrap;
              overflow: hidden;
            `}
          >
            {playBtnTitle ?? 'Play'}
          </QSTypography>
        </Box>
      </Box>
      {(title || subtitle || learnMoreHref) && (
        <Box
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
            align-self: stretch;
          `}
        >
          {(title || subtitle) && (
            <Box
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 4px;
                align-self: stretch;
              `}
            >
              {title && (
                <QSTypography
                  css={css`
                    align-self: stretch;
                    color: #2d2d2d;
                    font-family: Inter;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: ${isHovered ? 600 : 500};
                    line-height: 140%;
                    letter-spacing: -0.16px;
                  `}
                >
                  {title}
                </QSTypography>
              )}
              {subtitle && (
                <QSTypography
                  css={css`
                    align-self: stretch;
                    color: #7a7a7a;
                    font-family: Inter;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 140%; /* 19.6px */
                    letter-spacing: -0.14px;
                  `}
                >
                  {subtitle}
                </QSTypography>
              )}
            </Box>
          )}
          {learnMoreHref && (
            <Link
              css={css`
                align-self: stretch;
                color: #009de2;
                font-family: Inter;
                font-size: 14px;
                font-weight: 500;
                line-height: 140%;
                letter-spacing: -0.14px;
                display: flex;
                align-items: center;
                gap: 4px;

                &:hover {
                  color: #01579b;
                  #Vector {
                    fill: #01579b;
                  }
                }
              `}
              href={learnMoreHref}
              target="_blank"
              rel="nonreferrer"
              underline="none"
            >
              {`Learn more`} <ArrowOutwardIcon height={16} width={16} />
            </Link>
          )}
        </Box>
      )}
    </Link>
  );
};

export default TrainingVideoTile;
